'use client'

import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { ColorCode, Icon, Image } from '@vinted/web-ui'
import { ArrowRight16 } from '@vinted/monochrome-icons'

import { InfluencerTipsModel } from '@marketplace-web/domain/marketing-landing-page-data'

type Props = { data: InfluencerTipsModel }

export const NewTipsSection = ({ data: { cards, title } }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isFade, setIsFade] = useState(false)

  useEffect(() => {
    if (currentIndex === activeIndex) return

    setIsFade(true)

    setTimeout(() => {
      setIsFade(false)
    }, 1100)

    setTimeout(() => {
      setActiveIndex(currentIndex)
    }, 1000)
  }, [cards, currentIndex, activeIndex, isFade])

  const handleNextButtonClick = () => {
    setCurrentIndex(index => {
      const isFirstIndex = index === 0

      return isFirstIndex ? cards.length - 1 : index - 1
    })
  }

  const handlePreviousButtonClick = () => {
    setCurrentIndex(index => {
      const isLastIndex = index + 1 === cards.length

      return isLastIndex ? 0 : index + 1
    })
  }

  const renderButtonsNav = () => {
    return (
      <div className="influencer-v2__tips-card-nav-buttons">
        <button
          type="button"
          className="influencer-v2__tips-button influencer-v2__rotate-180"
          onClick={handleNextButtonClick}
          disabled={isFade}
        >
          <Icon
            name={ArrowRight16}
            color={isFade ? ColorCode.GreyscaleLevel3 : ColorCode.GreyscaleLevel1}
          />
        </button>
        <button
          type="button"
          className="influencer-v2__tips-button"
          onClick={handlePreviousButtonClick}
          disabled={isFade}
        >
          <Icon
            name={ArrowRight16}
            color={isFade ? ColorCode.GreyscaleLevel3 : ColorCode.GreyscaleLevel1}
          />
        </button>
      </div>
    )
  }

  const renderNumbersNav = () => {
    return (
      <div className="influencer-v2__tips-card-nav-numbers">
        {cards.map((_, index) => {
          const displayNumber = index < 9 ? `0${index + 1}` : `${index + 1}`
          const handleNumberClick = () => setCurrentIndex(index)

          return (
            <button
              type="button"
              key={index}
              className={classNames('', {
                'influencer-v2__tips-card-nav-number-disabled': currentIndex !== index,
                'influencer-v2__fade-in': currentIndex === index,
              })}
              onClick={handleNumberClick}
            >
              {displayNumber}
            </button>
          )
        })}
      </div>
    )
  }

  const renderImage = () => (
    <div className="influencer-v2-tips-card-images-wrapper">
      <div
        style={{ zIndex: 2 }}
        className={classNames('influencer-v2__tips-card-image', {
          'influencer-v2__fade-out': isFade,
        })}
      >
        <Image
          src={cards[activeIndex]?.image.x1Url}
          scaling={Image.Scaling.Cover}
          loading="lazy"
          testId={`tips-card-image-${activeIndex}`}
        />
      </div>
      <div style={{ zIndex: 1 }} className="influencer-v2__tips-card-image">
        <Image
          src={cards[currentIndex]?.image.x1Url}
          scaling={Image.Scaling.Cover}
          loading="lazy"
          testId={`tips-card-image-${currentIndex}`}
        />
      </div>
    </div>
  )

  const renderTitle = (titleText: string, index: number) => (
    <span
      className={classNames('influencer-v2__tips-card-title influencer-v2__fade-in', {
        'influencer-v2__block': index === currentIndex,
      })}
    >
      {titleText}
    </span>
  )

  const renderDescription = (description: string, index: number) => (
    <span
      className={classNames('influencer-v2__tips-card-description influencer-v2__fade-in', {
        'influencer-v2__block': index === currentIndex,
      })}
    >
      {description}
    </span>
  )

  return (
    <section data-testid="tips-section" className="influencer-v2__tips-section">
      <div className="influencer-v2__tips-inner">
        <div className="influencer-v2__tips-card" data-testid="influencer-v2__tips-card">
          {renderImage()}
          <div className="influencer-v2__tips-card-content">
            <div className="influencer-v2__tips-card-content-inner">
              <h2 className="influencer-v2__tips-title">{title}</h2>
              <div>
                <h3>{cards.map((card, cardIndex) => renderTitle(card.title, cardIndex))}</h3>
                <p>
                  {cards.map((card, cardIndex) => renderDescription(card.description, cardIndex))}
                </p>
              </div>
            </div>
            <div className="influencer-v2__tips-card-nav">
              {renderNumbersNav()}
              {renderButtonsNav()}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
